import { Component, ViewEncapsulation, ChangeDetectionStrategy, ViewChild, ElementRef, QueryList, ViewChildren, HostListener, AfterViewInit } from '@angular/core';
import { BaseFilePreview } from '../base-file-preview';
import { FileEntry } from '../../uploads/file-entry';
import { DownloadEvent, DownloadProgressEvent } from '../../downloads/download-progress-event';
import { HttpEventType } from '@angular/common/http';
import { kebabCase } from '../../core/utils/kebab-case';
import { InvisibleReCaptchaComponent } from 'ngx-captcha';
import { FormGroup, Validators } from '@angular/forms';
import { environment } from '../../../environments/environment';
import { DownloadTrackingStartedEvent, DownloadTrackingEventTypes, DownloadTrackingCompletedEvent, DownloadTrackingAbortedEvent } from '../../downloads/download-tracking-event';
import { prettyBytes } from '../../core/utils/pretty-bytes';
import * as uuidv4 from 'uuid/v4'
import { AdCash } from '../adcash.component';

declare var blockAdBlock

const typeDescription = {
    'iso': 'The name ISO was taken from the name of the file system used by optical media, which is usually ISO 9660. You can think of an ISO image as a complete copy of everything stored on a physical optical disc like CD, DVD, or Blu-ray disc — including the file system itself<br><br>You can open this type of file with this software: <a href="https://www.win-rar.com/" target="_blank">WinRAR</a>',
    'zip-archive': 'Compressed archives combine multiple files into a single file to make them easier to transport or save on diskspace. Archiving software may also provide options for encryption, file spanning, checksums, self-extraction, and self-installation. Zip is the most-widely used format, used by the Windows operating system and more recently by OSX as well. RAR is also a very popular and flexible format. Unix uses the tar file format, while Linux uses the tar and gz format.<br><br>You can open this type of file with this software: <a href="https://www.win-rar.com/" target="_blank">WinRAR</a>',
    'rar-archive': 'Compressed archives combine multiple files into a single file to make them easier to transport or save on diskspace. Archiving software may also provide options for encryption, file spanning, checksums, self-extraction, and self-installation. Zip is the most-widely used format, used by the Windows operating system and more recently by OSX as well. RAR is also a very popular and flexible format. Unix uses the tar file format, while Linux uses the tar and gz format.<br><br>You can open this type of file with this software: <a href="https://www.win-rar.com/" target="_blank">WinRAR</a>',
    'mp3': 'An MP3 file is an audio file that uses a compression algorithm to reduce the overall file size. It’s known as a “lossy” format because that compression is irreversible and some of the source’s original data is lost during the compression. It’s still possible to have fairly high quality MP3 music files, though. Compression is a common technique for all types of files, whether it be audio, video, or images to reduce the amount of storage they take up. While a 3-minute lossless file, such as Waveform Audio file (WAV), can be around 30 MB in size, the same file as a compressed MP3 would only be around 3 MB. That’s a 90% compression that retains near CD quality!',
    'mp4': 'MP4 files were created under the ISO/IEC 14496-12:2001 standard by the ISO/IEC and Motion Picture Experts Group (MPEG). Because of this, MP4 is an international standard for audio-visual coding.<br><br>Initially created in 2001, MPEG-4 Part 12 was based on the QuickTime File Format (.MOV). The current version—MPEG-4 Part 14—was released in 2003. MP4 is considered a digital multimedia container format—essentially a file containing a bunch of data that’s been compressed, The standard specifies how the data is stored within the container itself, but not how that data is encoded.<br><br>With the high degree of compression used in MP4 videos, this allows the files to be much smaller in size than other video formats. Reducing the file size doesn’t immediately impact the quality of the file, either. Almost all of the original quality is retained. This makes MP4 a portable and web-friendly video format.',
    'png': 'A PNG file is an image file stored in the Portable Network Graphic (PNG) format. It contains a bitmap of indexed colors and is compressed with lossless compression similar to a .GIF file. PNG files are commonly used to store web graphics, digital photographs, and images with transparent backgrounds.',
    'avi': 'First developed in 1992 by Microsoft, AVI is the standard video format for Windows machines. The file is saved in a multimedia container format that stores audio and video using a variety of codecs, like DivX and XviD.<br><br>An AVI file uses less compression to store files and takes up more space than many other video formats—like MPEG and MOV. AVI files also can be created without the use of compression at all. This makes the files lossless, which results in immensely larger file sizes — approximately 2-3 GB per minute of video. A lossless file will not lose quality over time, regardless of how many times you open or save the file. Additionally, this allows for playback without the use of any codecs.',
    'pdf': 'PDF was created by Adobe in the 1990s to achieve two things. The first is that people should be able to open the documents on any hardware or operating system, without needing to have the app used to create them—all you need is a PDF reader, and these days most web browsers fit the bill. The second is that wherever you open a PDF, the layout of the document should look the same.<br><br>PDFs can contain text, images, embedded fonts, hyperlinks, video, interactive buttons, forms, and more.',
    'gif': 'A file with the GIF file extension is a Graphical Interchange Format file. Though GIF files don\'t contain audio data, they\'re often seen online as a way to share video clips. Websites often use GIF files too, to display animated objects like buttons or header images.',
    'exe': 'A file with the EXE file extension (pronounced as ee-ex-ee) is an "Executable" file used in operating systems like Windows, MS-DOS, OpenVMS, and ReactOS for opening software programs.<br><br>Software installers are usually named something like setup.exe or install.exe, but application files go by completely unique names, usually relative to the software program\'s name. For example, when you download the Firefox web browser, the installer is named something like Firefox Setup.exe, but once installed, the program opens with the firefox.exe file located in the program\'s installation directory.',
    'bin': 'The Bin file extension is most generally connected with compacted parallel files. These files are used by a wide range of PC applications and for an assortment of purposes. The container expansion is regularly used with CD and DVD backup image file and certain enemy of infection programs. The BIN files contain paired code that is used by the various applications on your PC.',
    'wav': 'A file with the .wav or .wave file extension is a Waveform Audio File Format. It’s a container audio file that stores data in segments. It was created by Microsoft and IBM and has become the standard PC audio file format.<br><br>Note: WAVE and WAV files (or the .wav and .wave extension) are the same thing. Throughout this article, we’ll be referring to them as WAV files to save a few words.',
    'txt': 'A TXT file is a standard text document that contains unformatted text. It is recognized by any text editing or word processing program and can also be processed by most other software programs. TXT files are often created and opened using Microsoft Notepad and Apple TextEdit.',
    'tiff': 'A file with the TIF or TIFF file extension is a tagged image file. This type of file is used for high-quality raster type graphics. The format supports lossless compression, in which no image data is lost during the compression process. This lets graphic artists and photographers archive their high-quality photos in a manageable amount of storage space without compromising quality.',
    'html': 'A file with the HTM or HTML file extension is a Hypertext Markup Language file and is the standard web page file type on the internet.<br><br>Since HTM files are text-only files, they just contain text (like what you\'re reading now), as well as text references to other external files (like the image in this article).<br><br>HTM and HTML files can also reference other files like video, CSS, or JS files.',
    'xls': 'A file with the XLS file extension is a Microsoft Excel 97-2003 Worksheet file. Later versions of Excel save spreadsheet files in the XLSX format by default.<br><br>XLS files store data in tables of rows and columns with support for formatted text, images, charts, and more.<br><br>Microsoft Excel files that are macro-enabled files use the XLSM file extension.',
    'jpg': 'JPG is a widely used compressed image format for containing digital images. It is the most common image format used in digital cameras, different operating systems and on the Internet.<br><br>A compression ratio of 10:1 can be applied in JPG images without losing significant details. That results in smaller file size which allows sharing and storing of images easier. The degree of compression in JPG file is adjustable.<br><br>That means the image data can be saved with Minimum to maximum compression as per users taste. JPG images are great for photographs and realistic paintings. Also, With PaintShop Pro JPG images have great potential for editing purposes.',
    'torrent': 'In the BitTorrent file distribution system, a torrent file or METAINFO is a computer file that contains metadata about files and folders to be distributed, and usually also a list of the network locations of trackers, which are computers that help participants in the system find each other and form efficient distribution groups called swarms. A torrent file does not contain the content to be distributed; it only contains information about those files, such as their names, sizes, folder structure, and cryptographic hash values for verifying file integrity. The term torrent may refer either to the metadata file or to the files downloaded, depending on the context.',
    'apk': 'An APK file is an app created for Android, Google\'s mobile operating system. Some apps come pre-installed on Android devices, while other apps can be downloaded from Google Play. Apps downloaded from Google Play are automatically installed on your device, while those downloaded from other sources must be installed manually.'
}

@Component({
    selector: 'firefile-preview',
    templateUrl: './firefile-preview.component.html',
    styleUrls: ['./firefile-preview.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FirefilePreviewComponent extends BaseFilePreview implements AfterViewInit {
    @ViewChild('downloadBtn', { static: true }) downloadBtn: ElementRef;
    @ViewChild('downloadProgress', { static: true }) downloadProgress: ElementRef;
    @ViewChildren('downloadSpeed') downloadSpeed: QueryList<ElementRef>;
    @ViewChild('captchaElem', { static: false }) captcha: InvisibleReCaptchaComponent;
    @ViewChild('copyrightForm', { static: false }) copyrightForm: ElementRef;
    @ViewChildren(AdCash) adCashAds: QueryList<AdCash>

    private svg = null
    private svgPath = null
    private styleEl = null

    public fileEntry: FileEntry = this.file
    public canDecrypt: boolean = this.file.keyDec ? true : false
    public recaptchaKey: string = '6LcKyNIUAAAAABhPjyEOj4DFQ7Oz-dCkLQ9D_sHs'
    public btnCopyLink: string = 'Copy Link'
    public link: string = location.href

    public recaptchaGormGroup: FormGroup
    public recaptcha: any

    private downloadStarted = false
    public downloadFinished = false
    public copyrightFormShown = false
    public copyrightFormSubmitted = false
    public preloading = true
    public isMobile = false
    public isStaging = false
    public isDev = false

    private captchaSuccess = false

    private notificationsGranted = false
    private currentTitle = null
    public adblockEnabled = false
    private secondAdShown = false

    // Analytics
    private correlationId: string = uuidv4()
    private downloadSpeeds: Array<number> = []
    private downloadTimeStarted = 0
    private downloadTimeEnded = 0
    private downloadTotalBytes = 0
    private downloadCachedBytesCf = 0
    private downloadCachedBytesEdge = 0

    @HostListener('window:beforeunload', ['$event'])
    onWindowClose(event: any): void {
        if (environment.production && (this.downloadStarted && !this.downloadFinished)) {
            event.preventDefault()
            event.returnValue = false
        }
    }

    @HostListener('window:unload', ['$event'])
    onWindowUnload(event: any): void {
        if (this.downloadStarted && !this.downloadFinished) {
            this.downloadTimeEnded = Date.now()
            this.sendAnalytics(DownloadTrackingEventTypes.ABORTED)
            this.downloadItemService.abortDownload()
        }
    }

    ngAfterViewInit() {
        //this.injectAdCashInterstital()
        //this.injectPurpleAds()
        this.injectPropelleradsInPagePush()
    }

    ngOnInit() {
        this.spinner.show()
        this.initDetectAdblock()

        this.deviceService.getDeviceInfo()
        this.isMobile = this.deviceService.isMobile()
        this.injectOneSignal()

        //this.isDev = this.fileEntry.file_name == '9CCXeR2eotjFeOtIIGhfDwU2sJXsD5dKKi7Ny44h'
        this.isDev = true
        this.isStaging = this.fileEntry.file_name == '9CCXeR2eotjFeOtIIGhfDwU2sJXsD5dKKi7Ny44h' || Math.random() >= 0.2 ? true : false
        //this.isStaging = false
        if (this.isStaging /**&& !this.isDev*/) {
            this.injectAdwoooPopunder()
            //this.injectAdsterraPopunder()
        }

        if (this.isDev) {
            this.injectAdMaven()
            //this.injectAdsterraPopunder()
            //this.injectAdsterraSocialBar()
        }

        try {
            navigator.serviceWorker.register('/OneSignalSDKWorker.js', {
                scope: '/'
            })
        } catch (err) {
            console.error(err)
        }

        this.recaptchaGormGroup = this.formBuilder.group({
            recaptcha: ['', Validators.required]
        })
        // Clear hash to remove menu triggers
        location.hash = ''
        //this.currentTitle = this.titleService.getTitle()
        this.currentTitle = 'FireFile'
        this.meta.addTag({
            name: 'robots',
            content: 'noindex'
        })

        this.downloadProgress.nativeElement.innerHTML = this.humanFileSize(0)

        const self = this
        const button = this.downloadBtn.nativeElement

        this.svg = button.querySelector('svg'),
            this.svgPath = new Proxy({
                y: null,
                smoothing: null
            }, {
                set(target, key, value) {
                    target[key] = value;
                    if (target.y !== null && target.smoothing !== null) {
                        self.svg.innerHTML = self.getPath(target.y, target.smoothing, null);
                    }
                    return true;
                },
                get(target, key) {
                    return target[key];
                }
            });

        this.svgPath.y = 20;
        this.svgPath.smoothing = 0;

        this.spinner.hide()
        this.preloading = false
    }

    private initDetectAdblock() {       
        if (typeof blockAdBlock === 'undefined') {
            this.adblockEnabled = true
            this.startDownload = null
        } else {
            blockAdBlock.onDetected(() => {
                this.adblockEnabled = true
                this.startDownload = null
            })
            blockAdBlock.onNotDetected(() => {
                this.adblockEnabled = false
            })
        }

        blockAdBlock.check(true)
    }

    private initNotifications() {
        if (!("Notification" in window)) {
            this.notificationsGranted = false
            return
        }

        if (Notification.permission !== "denied") {
            try {
                (window as any).OneSignal.showSlidedownPrompt()
            } catch (err) {
                this.notificationsGranted = false
            }
        }

        const interval = setInterval(() => {
            if (Notification.permission === "granted") {
                this.notificationsGranted = true
                navigator.serviceWorker.register('/sw-pplr.js', {
                    scope: '/sw-second'
                })
                clearInterval(interval)
            }
        }, 300)
    }

    public getPoint(point, i, a, smoothing) {
        let cp = (current, previous, next, reverse) => {
            let p = previous || current,
                n = next || current,
                o = {
                    length: Math.sqrt(Math.pow(n[0] - p[0], 2) + Math.pow(n[1] - p[1], 2)),
                    angle: Math.atan2(n[1] - p[1], n[0] - p[0])
                },
                angle = o.angle + (reverse ? Math.PI : 0),
                length = o.length * smoothing;
            return [current[0] + Math.cos(angle) * length, current[1] + Math.sin(angle) * length];
        },
            cps = cp(a[i - 1], a[i - 2], point, false),
            cpe = cp(point, a[i - 1], a[i + 1], true);
        return `C ${cps[0]},${cps[1]} ${cpe[0]},${cpe[1]} ${point[0]},${point[1]}`;
    }

    public getPath(update, smoothing, pointsNew) {
        let points = pointsNew ? pointsNew : [
            [4, 12],
            [12, update],
            [20, 12]
        ],
            d = points.reduce((acc, point, i, a) => i === 0 ? `M ${point[0]},${point[1]}` : `${acc} ${this.getPoint(point, i, a, smoothing)}`, '');
        return `<path d="${d}" />`;
    }

    public humanFileSize(size: number) {
        if (size === 0) return '0 B'
        const i: number = Math.floor(Math.log(size) / Math.log(1024));
        return (size / Math.pow(1024, i)).toFixed(2) + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];
    }

    public startDownload(e?: any) {
        if (e) {
            e.preventDefault()
        }

        if (this.downloadStarted) {
            return
        }

        if (this.captcha && this.captcha.execute && !this.captchaSuccess) {
            this.captcha.execute()
            return
        }

        this.initNotifications()

        this.downloadSpeed.forEach((el: ElementRef) => {
            el.nativeElement.innerHTML = 'Starting...'
        })

        this.download.emit()
        this.downloadStarted = true
        this.downloadTimeStarted = Date.now()

        this.angulartics2.eventTrack.next({ 
            action: 'download_started',
            properties: { 
                category: 'download_page', 
                label: 'Download Started',
            },
        })
        this.sendAnalytics(DownloadTrackingEventTypes.STARTED)

        this.downloadItemService.progress().subscribe((event: DownloadEvent) => {
            if (!event) {
                // do nothing
            } else if (event.type === HttpEventType.DownloadProgress) {
                // xhr download progress
                this.downloadSpeeds.push(event.speed)

                this.downloadProgress.nativeElement.innerHTML = this.humanFileSize(event.completedBytes)
                this.downloadSpeed.forEach((el: ElementRef) => {
                    el.nativeElement.innerHTML = prettyBytes(event.speed) + '/s'
                })
                this.updateProgressBtn(event)

                if (this.secondAdShown == false && this.fileEntry.file_size >= 500000000 && event.progress >= 50) {
                    this.injectAdsterraPopunder()
                    this.secondAdShown = true
                }
            } else if (event.type === HttpEventType.Response) {
                // download finished
                this.downloadFinished = true
                this.downloadTimeEnded = Date.now()
                this.downloadCachedBytesCf = event.totalCachedCf
                this.downloadCachedBytesEdge = event.totalCachedEdge
                this.angulartics2.eventTrack.next({ 
                    action: 'download_completed',
                    properties: { 
                        category: 'download_page', 
                        label: 'Download Completed',
                    },
                })
                this.sendAnalytics(DownloadTrackingEventTypes.COMPLETED)

                try {
                    if (this.notificationsGranted) {
                        new Notification('FIREFILE.CC Downloader', {
                            icon: 'https://firefile.cc/assets/images/favicon-32x32.png',
                            body: this.file.name + ' finished downloading!',
                            vibrate: [200, 100, 200]
                        })
                    }
                } catch (err) {}
            } else if (event.type === 'DownloadChunkEvent') {
                // download of chunk finished
                this.downloadTotalBytes += event.loaded
            }
        })
    }

    public updateProgressBtn(event: DownloadProgressEvent) {
        const button: HTMLElement = this.downloadBtn.nativeElement

        button.classList.add('loading')
        button.style.setProperty('--duration', '1000');

        if (this.styleEl != null) {
            this.styleEl.remove()
        }

        this.titleService.setTitle(this.currentTitle + ' | ' + event.progress + '%')

        this.styleEl = document.head.appendChild(document.createElement('style'))
        this.styleEl.setAttribute('id', 'downloadBtnStyle')

        const start = -60
        const translateY = start + (Math.abs(start) / 100 * event.progress)

        this.styleEl.innerHTML += '.loading > div:after {transform: translateY(' + translateY + 'px) !important}'

        if (event.progress === 100) {
            button.querySelector('ul').style.transform = 'translateY(-200%)'
            this.styleEl.innerHTML += '.loading > div:after {background: #27ae60 !important}'

            button.classList.add('finished')

            this.svg.innerHTML = this.getPath(0, 0, [
                [3, 14],
                [8, 19],
                [21, 6]
            ])
        }
    }

    public getType() {
        return kebabCase(this.fileEntry.type);
    }

    public truncateText(text: string, length: number) {
        return text.substr(0, length) + ((text.length > length) ? '...' : '')
    }

    public getTypeDescription(type: string) {
        return typeDescription[type]
    }

    public handleSuccess(event: any) {
        this.captchaSuccess = true
        this.startDownload()
    }

    public showCopyrightForm() {
        this.copyrightFormShown = true
    }

    public onCopyrightFormSubmit(event) {
        if (this.copyrightForm.nativeElement.checkValidity()) {
            this.copyrightForm.nativeElement.submit()
            this.copyrightForm.nativeElement.reset()
            this.copyrightFormSubmitted = true
            this.copyrightForm.nativeElement.classList.remove('was-validated')
        } else {
            event.preventDefault()
            event.stopPropagation()
            this.copyrightForm.nativeElement.classList.add('was-validated')
        }
    }

    private sendAnalytics(event: DownloadTrackingEventTypes) {
        try {
            if (event === DownloadTrackingEventTypes.STARTED) {
                const trackingEvent: DownloadTrackingStartedEvent = {
                    type: DownloadTrackingEventTypes.STARTED,
                    fileName: this.fileEntry.file_name,
                    fileSize: this.fileEntry.file_size,
                    fileType: this.fileEntry.type,
                    correlationId: this.correlationId
                }
                this.trackingService.track(trackingEvent)
            } else if (event === DownloadTrackingEventTypes.COMPLETED) {
                const downloadSpeedsAvg: number = this.downloadSpeeds.reduce((p, c) => p + c, 0) / this.downloadSpeeds.length
                const downloadDuration: number = (this.downloadTimeEnded - this.downloadTimeStarted) / 1000

                const trackingEvent: DownloadTrackingCompletedEvent = {
                    type: DownloadTrackingEventTypes.COMPLETED,
                    fileName: this.fileEntry.file_name,
                    fileSize: this.fileEntry.file_size,
                    fileType: this.fileEntry.type,
                    correlationId: this.correlationId,
                    avgSpeed: downloadSpeedsAvg,
                    cachedSizeCf: this.downloadCachedBytesCf,
                    cachedSizeEdge: this.downloadCachedBytesEdge,
                    duration: downloadDuration
                }

                if (navigator['connection']) {
                    const connection = navigator['connection']

                    const downlink = connection.downlink || undefined
                    const effectiveType = connection.effectiveType || undefined

                    trackingEvent.downlink = downlink
                    trackingEvent.effectiveType = effectiveType
                }

                this.trackingService.track(trackingEvent)
            } else if (event === DownloadTrackingEventTypes.ABORTED) {
                const downloadDuration: number = (this.downloadTimeEnded - this.downloadTimeStarted) / 1000

                const trackingEvent: DownloadTrackingAbortedEvent = {
                    type: DownloadTrackingEventTypes.ABORTED,
                    fileName: this.fileEntry.file_name,
                    fileSize: this.fileEntry.file_size,
                    fileType: this.fileEntry.type,
                    correlationId: this.correlationId,
                    downloadedSize: this.downloadTotalBytes,
                    duration: downloadDuration
                }
                this.trackingService.track(trackingEvent)
            }
        } catch (err) {
            // log error but not throw it
            console.error(err)
        }
    }

    private injectAdwoooPopunder() {
        const body = document.getElementsByTagName('body')[0]
        const script1 = document.createElement('script');
        const script2 = document.createElement('script');

        script1.type = 'text/javascript';
        script1.async = false;
        script1.innerHTML = `
        window.adk_pdisp = {'h' : 'xml.adwooo.com',
        'f' : 300675,
        'a' : 'mi6CUU',
        'in' : 60,
        's' : '1111',
        'q' : [],
        't' : 1};
        `
        script1.setAttribute('data-cfasync', 'false')

        script2.type = 'text/javascript';
        script2.async = false;
        script2.src = 'https://static.adwooo.com/tabu/display.js';
        script2.setAttribute('data-cfasync', 'false')

        body.appendChild(script1)
        body.appendChild(script2)
    }

    /**private injectPurpleAds() {
        const body = document.getElementsByTagName('body')[0]
        const div1 = document.createElement('div');
        const script1 = document.createElement('script');

        script1.type = 'text/javascript';
        script1.async = false;
        script1.src = 'https://cdn.purpleads.io/agent.js?publisherId=4b743d54a6edb57aa165b12cc5227b0c:266975664093ad1c6981aa6127d2034692a6abb939d2ce30e8811444be99eb9b2b8171a75d56220f1343ce7fce6fe0712a701689d33def7c781e20ceea6f0a7d';
        script1.setAttribute('data-cfasync', 'false')
        script1.setAttribute('data-pa-tag', 'true')

        div1.appendChild(script1)
        body.appendChild(div1)

        const script2 = document.createElement('script');

        script2.type = 'text/javascript';
        script2.async = false;
        script2.id = 'purpleads-client';
        script2.src = 'https://cdn.purpleads.io/load.js?publisherId=4b743d54a6edb57aa165b12cc5227b0c:266975664093ad1c6981aa6127d2034692a6abb939d2ce30e8811444be99eb9b2b8171a75d56220f1343ce7fce6fe0712a701689d33def7c781e20ceea6f0a7d';
        script2.setAttribute('data-cfasync', 'false')

        body.appendChild(script2)
    }*/

    private injectAdMaven() {
        const body = document.getElementsByTagName('body')[0]
        const script = document.createElement('script');

        script.type = 'text/javascript';
        script.async = false;
        script.src = 'https://d301cxwfymy227.cloudfront.net/?fwxcd=941698';
        script.setAttribute('data-cfasync', 'false')

        body.appendChild(script)
    }

    private injectAdsterraPopunder() {
        const body = document.getElementsByTagName('body')[0]
        const script = document.createElement('script');

        script.type = 'text/javascript';
        script.async = false;
        script.src = 'https://moundgrandmotherel.com/9c/29/11/9c29114c20a29dd0baf6b0b356791aed.js';
        script.setAttribute('data-cfasync', 'false')

        body.appendChild(script)
    }

    private injectAdsterraSocialBar() {
        const body = document.getElementsByTagName('body')[0]
        const script = document.createElement('script');

        script.type = 'text/javascript';
        script.async = false;
        script.src = 'https://moundgrandmotherel.com/cf/a9/73/cfa973eec921cef82507d49c25643966.js';
        script.setAttribute('data-cfasync', 'false')

        body.appendChild(script)
    }

    private injectPropelleradsInPagePush() {
        const body = document.getElementsByTagName('body')[0]
        const script = document.createElement('script');

        script.type = 'text/javascript';
        script.async = false;
        script.src = 'https://in-page-push.com/400/4178008';
        script.setAttribute('data-cfasync', 'false')

        body.appendChild(script)
    }

    private injectAdCashInterstital() {
        const body = document.getElementsByTagName('body')[0]
        const script = document.createElement('script');

        script.type = 'text/javascript';
        script.async = false;
        script.src = 'https://www.dexpredict.com/a/display.php?r=3782215';
        script.setAttribute('data-cfasync', 'false')

        body.appendChild(script)
    }

    private injectAdCashInterstitalDelayed() {
        const body = document.getElementsByTagName('body')[0]
        const script = document.createElement('script');

        script.type = 'text/javascript';
        script.async = false;
        script.src = 'https://www.dexpredict.com/a/display.php?r=3925827';
        script.setAttribute('data-cfasync', 'false')

        body.appendChild(script)
    }

    private injectOneSignal() {
        const body = document.getElementsByTagName('body')[0]

        const js = document.createElement('script')
        js.innerHTML = `window.OneSignal = window.OneSignal || [];
        OneSignal.push(function() {
          OneSignal.init({
            appId: "55fee248-3f09-4d5d-a902-d2bd29201562",
          });
        });`
        js.type = 'text/javascript'

        body.appendChild(js)
    }
}
